import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Wrench, Plus, Check, Calendar, Bell, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_MAINTENANCE = [
  { id: 1, title: 'Byt luftfilter (ventilation)', interval: 90, category: 'Ventilation' },
  { id: 2, title: 'Rengör köksfläkt', interval: 30, category: 'Kök' },
  { id: 3, title: 'Testa brandvarnare', interval: 30, category: 'Säkerhet' },
  { id: 4, title: 'Avfrosta frys', interval: 180, category: 'Kök' },
  { id: 5, title: 'Rengör tvättmaskin', interval: 30, category: 'Tvätt' },
  { id: 6, title: 'Rengör diskmaskin', interval: 30, category: 'Kök' },
  { id: 7, title: 'Kontrollera vattenlås', interval: 180, category: 'VVS' },
  { id: 8, title: 'Byt tandborste/huvud', interval: 90, category: 'Badrum' },
  { id: 9, title: 'Service värmepump/AC', interval: 365, category: 'Värme/Kyla' },
  { id: 10, title: 'Rensa hängrännor', interval: 180, category: 'Utomhus' },
  { id: 11, title: 'Kontrollera rökvarnare batteri', interval: 180, category: 'Säkerhet' },
  { id: 12, title: 'Rengör ugn', interval: 60, category: 'Kök' },
];

export default function MaintenancePage() {
  const navigate = useNavigate();
  const { hasAccess, getAuthHeader } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState('');
  const [newInterval, setNewInterval] = useState(30);

  useEffect(() => {
    if (hasAccess) {
      fetchTasks();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/maintenance`, { headers: getAuthHeader() });
      if (res.data.length === 0) {
        // Initialize with default tasks
        const initialized = DEFAULT_MAINTENANCE.map(t => ({
          ...t,
          lastDone: null,
          nextDue: null
        }));
        setTasks(initialized);
      } else {
        setTasks(res.data);
      }
    } catch (error) {
      // Use local state if API doesn't exist yet
      const saved = localStorage.getItem('maintenance-tasks');
      if (saved) {
        setTasks(JSON.parse(saved));
      } else {
        const initialized = DEFAULT_MAINTENANCE.map(t => ({
          ...t,
          lastDone: null,
          nextDue: null
        }));
        setTasks(initialized);
        localStorage.setItem('maintenance-tasks', JSON.stringify(initialized));
      }
    } finally {
      setLoading(false);
    }
  };

  const markDone = (taskId) => {
    const today = new Date().toISOString().split('T')[0];
    const updatedTasks = tasks.map(t => {
      if (t.id === taskId) {
        const nextDue = new Date();
        nextDue.setDate(nextDue.getDate() + t.interval);
        return {
          ...t,
          lastDone: today,
          nextDue: nextDue.toISOString().split('T')[0]
        };
      }
      return t;
    });
    setTasks(updatedTasks);
    localStorage.setItem('maintenance-tasks', JSON.stringify(updatedTasks));
    toast.success('Markerad som klar!');
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    const task = {
      id: Date.now(),
      title: newTask,
      interval: newInterval,
      category: 'Övrigt',
      lastDone: null,
      nextDue: null
    };
    const updatedTasks = [...tasks, task];
    setTasks(updatedTasks);
    localStorage.setItem('maintenance-tasks', JSON.stringify(updatedTasks));
    setNewTask('');
    toast.success('Uppgift tillagd!');
  };

  const deleteTask = (taskId) => {
    const updatedTasks = tasks.filter(t => t.id !== taskId);
    setTasks(updatedTasks);
    localStorage.setItem('maintenance-tasks', JSON.stringify(updatedTasks));
    toast.success('Uppgift borttagen');
  };

  const getDueStatus = (task) => {
    if (!task.nextDue) return 'pending';
    const today = new Date();
    const due = new Date(task.nextDue);
    const daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return 'overdue';
    if (daysUntil <= 7) return 'soon';
    return 'ok';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Aldrig';
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  // Group by category
  const categories = [...new Set(tasks.map(t => t.category))];

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="maintenance-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Hemunderhåll</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till hemunderhåll.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="maintenance-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Hemunderhåll</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-2">
          <Wrench className="w-12 h-12 mx-auto text-amber-500" />
          <h2 className="font-heading text-2xl font-semibold">Hemunderhåll</h2>
          <p className="text-muted-foreground">Håll koll på service och underhåll i hemmet</p>
        </div>

        {/* Add new task */}
        <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
          <h3 className="font-semibold">Lägg till uppgift</h3>
          <div className="flex gap-2">
            <Input
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder="T.ex. Byt däck på bilen"
              className="flex-1"
            />
            <select
              value={newInterval}
              onChange={(e) => setNewInterval(Number(e.target.value))}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value={7}>Varje vecka</option>
              <option value={14}>Varannan vecka</option>
              <option value={30}>Varje månad</option>
              <option value={60}>Varannan månad</option>
              <option value={90}>Var 3:e månad</option>
              <option value={180}>Var 6:e månad</option>
              <option value={365}>Varje år</option>
            </select>
            <Button onClick={addTask} className="rounded-xl px-4" data-testid="add-maintenance-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              Lägg till
            </Button>
          </div>
        </div>

        {/* Tasks by category */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {categories.map(category => (
              <div key={category}>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">{category}</h3>
                <div className="space-y-2">
                  {tasks.filter(t => t.category === category).map(task => {
                    const status = getDueStatus(task);
                    return (
                      <div
                        key={task.id}
                        className={`bg-white p-4 rounded-xl border flex items-center gap-4 ${
                          status === 'overdue' ? 'border-red-200 bg-red-50' :
                          status === 'soon' ? 'border-amber-200 bg-amber-50' :
                          'border-border/30'
                        }`}
                      >
                        <button
                          onClick={() => markDone(task.id)}
                          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                            task.lastDone ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-amber-100 hover:text-amber-600'
                          }`}
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <div className="flex-1">
                          <h4 className="font-medium">{task.title}</h4>
                          <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Senast: {formatDate(task.lastDone)}
                            </span>
                            {task.nextDue && (
                              <span className={`flex items-center gap-1 ${status === 'overdue' ? 'text-red-600' : status === 'soon' ? 'text-amber-600' : ''}`}>
                                <Bell className="w-3 h-3" />
                                Nästa: {formatDate(task.nextDue)}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tips */}
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
          <p className="text-amber-800 text-sm">
            💡 <strong>Tips:</strong> Regelbundet underhåll förlänger livslängden på dina saker och förebygger dyra reparationer.
          </p>
        </div>
      </main>
    </div>
  );
}
